import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import forge from 'apis/forge';
import TopBar from './TopBar';
import TabBar from './TopBar/TabBar';
import FoundationsView from './sections/FoundationsView';
import ThemeView from './sections/ThemeView';
import StylesView from './sections/StylesView';
import ComponentsView from './sections/ComponentsView';
import GalleryView from './sections/GalleryView';
import GuidelinesView from './sections/GuidelinesView';
import AssetsView from './sections/AssetsView';
import HomeView from './sections/HomeView';
import PreparingView from './sections/PreparingView';
import { TokenStylesInjector } from './sections/tokens/TokenStylesInjector';
import * as S from './styles';
import * as SectionS from './sections/styles';
import { shallowEqual } from 'react-redux';
import DesignSystemLeftPanel from '../Panel/DesignSystemLeftPanel';
import { DesignSystemSection, VALID_DESIGN_SYSTEM_SECTIONS } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/config';
import { addDsTab, setActiveDsTab, updateDsTab } from 'redux/mode/actions';
import { displayPromptModal } from 'redux/mode/util/modals';

const DesignSystemEditor = () => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		designSystemContent,
		designSystemContentLoading,
		dsTabs,
		activeTab,
		isPreparing,
		activeDesignSystemId,
	} = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			designSystemContentLoading: r.db.loading.activeDesignSystemVersion,
			dsTabs: r.mode.dsTabs,
			activeTab: selectActiveDsTab(r.mode),
			isPreparing: r.db.dsPreparing,
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);

	const hasShownRenameRef = useRef(false);

	const isValidSection = (value: string | null): value is DesignSystemSection => {
		return value !== null && VALID_DESIGN_SYSTEM_SECTIONS.includes(value as DesignSystemSection);
	};

	const activeSection = activeTab?.section || 'foundations';
	const activeContentPath = activeTab?.contentPath;

	// Sync URL with active section and contentPath (only when there's an active tab)
	useEffect(() => {
		if (activeTab) {
			const params: Record<string, string> = { section: activeSection };
			if (activeContentPath) {
				params.contentPath = activeContentPath;
			}
			setSearchParams(params);
		} else {
			// Clear URL params when no tab is active (showing home)
			setSearchParams({});
		}
	}, [activeTab, activeSection, activeContentPath, setSearchParams]);

	// Initialize tabs from URL on mount if URL param doesn't match current active tab
	useEffect(() => {
		const sectionParam = searchParams.get('section');
		const contentPathParam = searchParams.get('contentPath');
		if (!isValidSection(sectionParam)) return;

		// Assets always uses a single tab regardless of contentPath
		const tabId =
			sectionParam === 'assets'
				? 'section-assets'
				: contentPathParam
				? `${sectionParam}-${contentPathParam}`
				: `section-${sectionParam}`;

		// Skip if the active tab already matches the URL (avoids feedback loop with URL sync effect)
		if (activeTab?.id === tabId && activeTab?.contentPath === (contentPathParam || undefined))
			return;

		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			// For assets, update contentPath if it changed
			if (
				sectionParam === 'assets' &&
				contentPathParam &&
				existingTab.contentPath !== contentPathParam
			) {
				dispatch(updateDsTab({ id: tabId, contentPath: contentPathParam }));
			}
			if (existingTab.id !== activeTab?.id) {
				dispatch(setActiveDsTab(tabId));
			}
		} else {
			// Tab doesn't exist, create it with proper label
			const chrome = DS_TAB_CHROME[sectionParam];
			let label = chrome?.label || sectionParam.charAt(0).toUpperCase() + sectionParam.slice(1);

			// If contentPath provided, try to find a matching label from content
			if (contentPathParam && designSystemContent && sectionParam !== 'assets') {
				if (sectionParam === 'guidelines') {
					const guideline = designSystemContent.guidelines?.find(
						(g) => g.path === contentPathParam
					);
					if (guideline) label = guideline.title;
				} else if (sectionParam === 'components') {
					const component = designSystemContent.components?.all?.find(
						(c) => c.id === contentPathParam
					);
					if (component) label = component.name;
				} else if (sectionParam === 'gallery') {
					const gallery = designSystemContent.gallery?.find((g) => g.path === contentPathParam);
					if (gallery) label = gallery.title;
				}
			}

			dispatch(
				addDsTab({
					id: tabId,
					section: sectionParam,
					contentPath: contentPathParam || undefined,
					label,
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- activeTab and dsTabs intentionally excluded; a separate effect syncs URL on activeTab changes
	}, [searchParams, designSystemContent, dispatch]);

	// Show rename prompt for newly created design systems
	useEffect(() => {
		if (hasShownRenameRef.current) return;
		if (searchParams.get('new') !== 'true') return;
		if (!activeDesignSystemId) return;
		hasShownRenameRef.current = true;

		const dsId = activeDesignSystemId;
		setSearchParams({}, { replace: true });
		displayPromptModal({
			title: 'Name your design system',
			placeholder: 'e.g. Acme Design System',
			submitLabel: 'Save',
			initialValue: DEFAULT_DS_TITLE,
			validate: (v: string) => (v.trim() ? null : 'Name cannot be empty'),
			onSubmit: (value) => {
				if (value) forge.updateDesignSystem({ update: { id: dsId, title: value.trim() } });
			},
		});
	}, [searchParams, activeDesignSystemId, setSearchParams]);

	const renderSectionView = () => {
		// Show PreparingView when DS is being prepared for first time
		if (isPreparing) {
			return <PreparingView />;
		}

		// Show HomeView when no active tab
		if (!activeTab) {
			return <HomeView />;
		}

		// Show loading state while DS content is being loaded
		if (designSystemContentLoading === 'loading') {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Loading Design System...</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>Loading design system content...</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		// Show empty state if content hasn't loaded yet
		if (designSystemContentLoading === 'never-loaded' || !designSystemContent) {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Design System</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>
							No design system content available. If you think this is an error, please contact
							hello@moonchild.ai for assistance.
						</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		switch (activeSection) {
			case 'foundations':
				return <FoundationsView />;
			case 'theme':
				return <ThemeView />;
			case 'styles':
				return <StylesView />;
			case 'components':
				return <ComponentsView />;
			case 'gallery':
				return <GalleryView />;
			case 'guidelines':
				return <GuidelinesView />;
			case 'assets':
				return <AssetsView />;
			default:
				return <FoundationsView />;
		}
	};

	return (
		<S.Container>
			<TokenStylesInjector />
			<S.TopBarWrapper>
				<TopBar />
			</S.TopBarWrapper>
			<S.ContentWrapper>
				<S.LeftPanel>
					<DesignSystemLeftPanel />
				</S.LeftPanel>
				<S.MainContent>
					{!isPreparing && <TabBar />}
					<S.SectionContentWrapper>{renderSectionView()}</S.SectionContentWrapper>
				</S.MainContent>
				<S.RightPanel>{/* Reserved for detail panel */}</S.RightPanel>
			</S.ContentWrapper>
		</S.Container>
	);
};

export default DesignSystemEditor;
